<template>
  <div class="table-settings">
    <div class="table-list-header">
      <div class="text-2xl font-semibold text-vp-base-900">
        {{ $gettext('Team') }}
      </div>
      <router-link
        :to="{ name: 'UserAccountNew' }"
        class="hover:no-underline"
      >
        <div
          class="flex items-center justify-center fixed bottom-16 right-16 w-14 h-14 sm:h-37 sm:w-30 sm:static bg-vp-primary-900 rounded-full sm:rounded-lg"
        >
          <span class="hidden sm:inline-block text-vp-base-100 text-sm font-semibold">{{ $gettext('Add') }}</span>
          <Plus class="sm:hidden text-vp-base-100 h-6 w-6" />
        </div>
      </router-link>
    </div>

    <div
      v-if="userAccountLoading === LoadingState.LOADING"
      class="text-center"
    >
      {{ $gettext('Loading...') }}
    </div>

    <div
      v-else
      class="table-list"
    >
      <div class="table-list-item">
        <div class="header-name">
          {{ $gettext('Name') }}
        </div>
        <div class="header-name hidden lg:block">
          {{ $gettext('Email') }}
        </div>
        <div class="header-name">
          {{ $gettext('Role') }}
        </div>
        <div class="header-name hidden sm:block">
          {{ $gettext('Active') }}
        </div>
        <div class="header-name" />
      </div>
      <UserAccountRow
        v-for="userAccount in usersAccounts"
        :key="userAccount.id"
        :user-account="userAccount"
        @delete="triggerDeleteConfirmation"
      />
    </div>
    <VPModal
      :show="showDeleteConfirmation"
      :title="$gettext('Confirm User Account Deletion')"
      :confirm-label="$gettext('Delete')"
      :close-label="$gettext('Cancel')"
      size="sm"
      @confirm="confirmDeleteUserAccount"
      @close="closeModal"
    >
      <p class="text-center text-vp-base-900">
        {{ $gettext(`Are you sure you want to delete user account %{modal_text}`, { modal_text: modalText }) }}?
      </p>
    </VPModal>
  </div>
</template>

<script setup lang="ts">
import { useUserAccountsStore } from '@/stores/user-accounts';
import { Plus } from 'mdue';
import { computed, ref } from 'vue';
import { useRootStore } from '@/stores/root';
import { useToast } from 'vue-toastification';
import { useGettext } from 'vue3-gettext';
import VPModal from '@/components/form/VPModal.vue';
import type { UserAccount } from '@/types/UserAccount';
import { LoadingState } from '@/util/LoadingState';
import UserAccountRow from './UserAccountRow.vue';

const rootStore = useRootStore();
const userStore = useUserAccountsStore();
userStore.fetchAllUsersAccounts(rootStore.currentAccountId);
const usersAccounts = computed(() => userStore.usersAccounts);
const userAccountLoading = computed(() => userStore.userAccountLoading);
const toast = useToast();
const { $gettext } = useGettext();
const showDeleteConfirmation = ref(false);
const modalText = ref('');
const selectedUserAccountId = ref<number | undefined>();

const triggerDeleteConfirmation = (userAccount: UserAccount) => {
  selectedUserAccountId.value = userAccount.id;
  modalText.value = userAccount.user?.name as string;
  showDeleteConfirmation.value = true;
};

const closeModal = () => {
  showDeleteConfirmation.value = false;
};

const confirmDeleteUserAccount = async () => {
  try {
    const userAccountId = selectedUserAccountId.value as number;
    const { success, errors } = await userStore.deleteUserAccount({ userAccountId: userAccountId });
    if (errors) {
      errors.map(toast.error);
      return;
    }
    if (success) {
      toast.success($gettext('Successfully removed team member'));
      userStore.fetchAllUsersAccounts(rootStore.currentAccountId);
    }
    return;
  } finally {
    showDeleteConfirmation.value = false;
  }
};
</script>
